import React from 'react';
import axios from 'axios';
import { isEmpty } from 'lodash';

import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { ExposureModel } from '~/components/types/exposure-types';
import { getLocalDateTime } from '~/DateTimeUtils';
import type { ContactBaseModel } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

interface UsePreSubmitChecksProps {
  claim: ClaimPolymorph;
  exposure: ExposureModel;
  payableType: string;
  currentPaymentRequestId?: number;
}

interface HandlePreSubmitChecksParams {
  values: {
    payment_method: string;
    request_amount?: number;
    amount?: number;
    primary_payee_id?: number;
    request_extra?: Record<string, unknown>[];
    request_currency?: string;
    conversion_rate?: number;
    conversion_date?: string | Date;
  };
  payeeContacts?: ContactBaseModel[];
  isApprovalFlow?: boolean;
}

interface PreSubmitCheckResultsDialogState {
  open: boolean;
  preSubmitChecks?: Record<string, unknown>[];
}

const usePreSubmitChecks = ({
  claim,
  exposure,
  payableType,
  currentPaymentRequestId,
}: UsePreSubmitChecksProps): {
  handlePreSubmitChecks: (params: HandlePreSubmitChecksParams) => Promise<boolean>;
  showPreSubmitCheckResultsDialog: PreSubmitCheckResultsDialogState;
  setShowPreSubmitCheckResultsDialog: React.Dispatch<React.SetStateAction<PreSubmitCheckResultsDialogState>>;
} => {
  const [showPreSubmitCheckResultsDialog, setShowPreSubmitCheckResultsDialog] =
    React.useState<PreSubmitCheckResultsDialogState>({ open: false });

  const handlePreSubmitChecks = async ({
    values,
    payeeContacts = [],
    isApprovalFlow = false,
  }: HandlePreSubmitChecksParams): Promise<boolean> => {
    try {
      const preSubmitChecks = await axios.get(`/api/v1/claims/${claim.id}/payment_pre_submit_checks`, {
        params: {
          exposure_id: exposure.id !== 0 ? exposure.id : undefined,
          contact_ids: payeeContacts.map((contact) => contact.id),
          payment_method: values.payment_method,
          payable_type: payableType,
          amount: isApprovalFlow ? values.request_amount : values.amount,
          primary_payee_id: values.primary_payee_id,
          request_extra: values.request_extra,
          is_approval_flow: isApprovalFlow,
          current_payment_request_id: currentPaymentRequestId,
          request_currency: values.request_currency,
          conversion_rate: values.conversion_rate,
          conversion_date: values.conversion_date ? getLocalDateTime(values.conversion_date) : undefined,
        },
      });

      if (!isEmpty(preSubmitChecks?.data)) {
        setShowPreSubmitCheckResultsDialog({
          open: true,
          preSubmitChecks: preSubmitChecks.data,
        });
        return true; // Indicates checks were triggered and submission should stop
      }
    } catch (error) {
      await reportAxiosError(error);
      return true; // Treat error as a blocker too
    }

    return false; // No dialog needed, continue the submission
  };

  return {
    handlePreSubmitChecks,
    showPreSubmitCheckResultsDialog,
    setShowPreSubmitCheckResultsDialog,
  };
};

export default usePreSubmitChecks;
